import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Observable } from 'rxjs';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { CoAppTypeEnum, ILoanApplication } from 'src/app/core/services/loan-application/loan-application.model';
import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { MetadataEnum } from 'src/app/core/services/metadata/metadata.model';
import { MetadataService } from 'src/app/core/services/metadata/metadata.service';
import { IMetadata, MobileApiService } from 'src/app/core/services/mobile-api';
import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import { TagDataService } from 'src/app/core/services/tag-data/tag-data.service';
import {
	emailValidator,
	firstNameValidator,
	lastNameValidator,
	phoneValidator
} from 'src/app/shared/validators/form-validators';
import {
	CoAppConfirmSheetActions,
	CoAppConfirmSheetComponent
} from '../app-co-app-confirm-sheet/co-app-confirm-sheet.component';
import { PhoneTypeEnum, PhoneUtils } from 'src/app/core/utils/phone-utils';
import { TranslocoService } from '@ngneat/transloco';
import { LanguageEnum } from 'src/app/core/services/language/language.service';
import { QuestionTypeEnum } from '../co-app-common-questions/co-app-common-questions.component';

@Component({
	selector: 'op-co-app-invite',
	templateUrl: './co-app-invite.component.html',
	styleUrls: ['./co-app-invite.component.scss']
})
export class CoAppInviteComponent implements OnInit {
	private _loanApp: ILoanApplication;
	showSplashScreen: boolean = false;
	formGroup: FormGroup;
	needStrongCredit: boolean;
	toggleLanguageOptions$: Observable<IMetadata[]>;
	questionTypeEnum = QuestionTypeEnum;
	constructor(
		private formBuilder: FormBuilder,
		private metadataService: MetadataService,
		private routingService: RoutingService,
		private mobileService: MobileApiService,
		private loanAppService: LoanApplicationService,
		private bottomSheet: MatBottomSheet,
		private tagDataService: TagDataService,
		private dialogService: DialogService,
		protected translocoService: TranslocoService
	) {}

	doesMaxApplicantsReached: boolean;

	ngOnInit(): void {
		this.tagDataService.view(
			{},
			{
				event_action: 'On Page Load',
				event_category: 'co_app_invite_screen_on_load',
				event_label: 'Co-Applicant Invite Screen Loaded'
			}
		);

		this._loanApp = this.loanAppService.getLoanApp();
		this.needStrongCredit = this._loanApp.coAppType === CoAppTypeEnum.CECR;

		this.toggleLanguageOptions$ = this.metadataService.select(MetadataEnum.Language);
		this.mobileService.getCoApplicantDetails(this._loanApp.id).subscribe({
			next: (resp) => {
				this.doesMaxApplicantsReached = resp.length > 15;
			}
		});

		this.createForm(this.formBuilder);
	}

	showMaxApplicantsReachedWarning(): void {
		this.tagDataService.link(
			{},
			{
				event_action: 'Button Click',
				event_category: 'co_app_invite_max_limit_reached',
				event_label: 'Max Co-Applicants Reached'
			}
		);
		const message = `<p>${this.translocoService.translate('CO_APP.INVITE.maxCoApplicantsReached')}</p>`;
		const data = {
			title: this.translocoService.translate('CO_APP.OFFER.ADD_COAPPLICANT.header'),
			message,
			confirmText: this.translocoService.translate('GLOBAL.ok'),
			panelClass: 'co-app-max-applicant-dialog-panel'
		};
		this.dialogService.openSimpleMessageDialog(data);
	}

	onSubmit(): void {
		this.tagDataService.link(
			{},
			{
				event_category: `co_app_send_invite`,
				event_label: 'Invite Co-Applicant',
				event_action: 'Button Click'
			}
		);
		this.bottomSheet
			.open(CoAppConfirmSheetComponent, {
				panelClass: 'co-app-sheet-panel',
				data: this.formGroup.value
			})
			.afterDismissed()
			.subscribe({
				next: (action: CoAppConfirmSheetActions) => {
					this.tagDataService.link(
						{},
						{
							event_category: 'co_app_details_pop_up_action',
							event_label: 'Co-Applicant Pop up Action',
							action: action
						}
					);
					action === CoAppConfirmSheetActions.SEND_INVITE && this.saveCoApplicantDetails();
				}
			});
	}

	saveCoApplicantDetails() {
		let coAppDetails = this.formGroup.value;
		coAppDetails = {
			...this.formGroup.value,
			phoneNumber: PhoneUtils.formatPhoneNumber(coAppDetails.phoneNumber),
			phoneType: PhoneTypeEnum.cell,
			loanApplicationId: this._loanApp.id
		};
		this.mobileService.setCoApplicantDetails(this._loanApp.id, coAppDetails).subscribe({
			next: (resp) => {
				this.tagDataService.link(
					{},
					{
						event_category: 'co_app_details_saved',
						event_label: 'Co-Applicant Details Saved',
						event_action: 'Button Click'
					}
				);
				this.routingService.route(RoutingPathsEnum.coAppInviteSent);
			},
			error: (err) => {
				const error = err.error[0] ? err.error[0].msg : '';
				error === 'loanApplication.coAppInvite.exceedsLimit'
					? this.showMaxApplicantsReachedWarning()
					: this.dialogService.openErrorDialog(err).subscribe();
			}
		});
	}

	createForm(fb: FormBuilder): void {
		this.formGroup = fb.group({
			firstName: ['', [Validators.required, firstNameValidator(), Validators.minLength(2), Validators.maxLength(32)]],
			lastName: ['', [Validators.required, lastNameValidator(), Validators.minLength(2), Validators.maxLength(32)]],
			phoneNumber: ['', [Validators.required, phoneValidator()]],
			emailAddress: ['', [Validators.required, emailValidator()]],
			preferredLanguage: [LanguageEnum.english.toUpperCase(), Validators.required]
		});
	}
}
